"use client";

import { CommonExceptionLogCreateInputApiModel } from "@/Apis/ClinicApi";
import { SmartImage, SmartLink } from "@/Components";
import { reportClientExceptionLog } from "@/Helpers/clientExceptionLogReporter";
import { createErrorLogParameters } from "@/Helpers/errorLogParameters";
import * as Sentry from "@sentry/nextjs";
import { FC, useEffect } from "react";

/* ===============================
   Utils
================================ */

const MAX_LEN = 16_000;
const COOLDOWN_MS = 2000;

let isSending = false;
let lastSentAt = 0;
let lastFingerprint = "";
let lastFingerprintAt = 0;

function truncate(value?: string, max = MAX_LEN) {
  if (!value) return value;
  return value.length > max ? value.slice(0, max) + "…[truncated]" : value;
}

function fingerprint(error: Error) {
  const msg = (error.message ?? "").slice(0, 200);
  const stackHead = error.stack?.split("\n").slice(0, 3).join("\n") ?? "";
  return msg + "|" + stackHead;
}

/* ===============================
   Report to SQL (via Next API)
================================ */

async function reportClientError(error: Error, sentryEventId?: string) {
  const now = Date.now();

  // 1️⃣ جلوگیری از لوپ و اسپم
  if (isSending) return;
  if (now - lastSentAt < COOLDOWN_MS) return;

  // 2️⃣ dedupe (همون خطا پشت سر هم)
  const fp = fingerprint(error);
  if (fp === lastFingerprint && now - lastFingerprintAt < 60_000) return;

  lastFingerprint = fp;
  lastFingerprintAt = now;

  isSending = true;
  lastSentAt = now;

  try {
    const model: CommonExceptionLogCreateInputApiModel = {
      // مسیر و محیط
      Environment: process.env.NEXT_PUBLIC_ENV,
      Url: typeof window !== "undefined" ? window.location.href : undefined,
      Method: undefined, // برای client لازم نیست
      StatusCode: undefined, // client نداره

      // اینا رو عمداً از client نمی‌فرستیم
      Ip: undefined, // ← در بک‌اند پر می‌شود
      // Domain: undefined, // ← در بک‌اند از header / context
      UserAgent: navigator.userAgent,

      // اطلاعات خطا
      Message: truncate(error?.message ?? "Unknown error"),
      StackTrace: truncate(error?.stack),
      Source: "client-error-page",
      Type: error?.name ?? "Error",
      Data: undefined, // اگر خواستی later extra بزاری

      // لاگ و تحلیل
      Fingerprint: fingerprint(error),
      SentryEventId: sentryEventId,
      Parameters: [
        ...createErrorLogParameters(error),
        { Key: "Browser.Location", Value: window.location.href },
        { Key: "Browser.Pathname", Value: window.location.pathname },
        { Key: "Browser.Referrer", Value: document.referrer || undefined },
        { Key: "Browser.Language", Value: navigator.language },
        { Key: "Browser.UserAgent", Value: navigator.userAgent },
      ],
    };

    reportClientExceptionLog(model);
  } catch {
    // ❌ عمداً هیچ لاگی اینجا نمی‌زنیم
  } finally {
    isSending = false;
  }
}

/* ===============================
   Global Error Component
================================ */
interface ErrorProps {
  error: Error;
  reset: () => void;
}

const ErrorPage: FC<ErrorProps> = ({ error, reset }) => {
  useEffect(() => {
    if (process.env.NODE_ENV !== "production") {
      console.error(error);
      return;
    }
    // 1️⃣ ارسال به Sentry
    const eventId = Sentry.captureException(error);

    // 2️⃣ ارسال امن به SQL
    reportClientError(error, eventId);
  }, [error]);

  return (
    <div className="container-fluid p-0">
      <div className="w-100 overflow-hidden position-relative flex-wrap d-block vh-100">
        <div className="row justify-content-center align-items-center vh-100 overflow-auto flex-wrap">
          <div className="col-lg-8 col-md-12 text-center">
            <div className="error-info">
              <div className="error-404-img">
                <SmartImage
                  width={510}
                  height={450}
                  src="/Templates/Doccure/img/error-500.png"
                  className="img-fluid"
                  alt="خطای سرور ۵۰۰"
                />
                <div className="error-content">
                  <h5 className="mb-2">خطایی در پردازش درخواست رخ داده است</h5>
                  <p>
                    متأسفانه در حال حاضر مشکلی در سرور به وجود آمده است. لطفاً
                    چند لحظه بعد دوباره تلاش کنید.
                  </p>

                  <div className="d-flex justify-content-center gap-2">
                    <button
                      type="button"
                      onClick={reset}
                      className="btn btn-primary-gradient btn-sm"
                    >
                      تلاش مجدد
                    </button>

                    <SmartLink
                      href="/"
                      className="btn btn-outline-secondary btn-sm"
                    >
                      بازگشت به صفحه اصلی
                    </SmartLink>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ErrorPage;
